import React from "react"
import ApiService from "./ApiService"
import {embeddedSelection,filterEmbeddedEvents} from './embedded-events'

// Récupère la vue publique UNIFIÉE des événements d'une org depuis l'API dédiée
// GET /v1/events/:identifier : les "Affiches" (collection `events`, ex-legacy sorti
// de org.events[]) ET les "Parcours d'événement" (PE), chacun tagué
// kind:"AFFICHE"|"PE", plus les groupes multi-session (eventGroupes) et les groupes
// "système" par extension (eventGroupesByExtension, ex. Shabbat).
//
// ⚠️ Modèle GALERIES / ACTUALITÉS : ce fetch se fait CÔTÉ CLIENT (dans le
// componentDidMount/useEffect des composants événement), PAS au SSR — pour ne
// jamais bloquer le TTFB des pages qui n'affichent aucun événement. Les événements
// ne sont donc plus dans le payload org ni dans le HTML SSR (comme les galeries).
//
// Déduplication : plusieurs composants événement peuvent coexister sur une même
// page (ex. preview home + carrousel). Un cache mémoire par org (avec la promesse
// en vol) garantit UN SEUL appel réseau par org et par chargement de page.
//
// Fail-safe : renvoie toujours une structure exploitable ({events:[], ...}) — un
// échec réseau/API laisse simplement la liste vide, aucun composant ne casse.

const EMPTY = {events: [], eventGroupes: [], eventGroupesByExtension: {}}

// Cache mémoire (par org.id) des promesses en vol / résolues, pour dédupliquer les
// appels concurrents d'une même page. Vidé au rechargement (module rechargé).
const cache = new Map()

export function fetchEvents(orgId, {force = false, virtualId = null} = {}) {
    if (!orgId) return Promise.resolve({...EMPTY})

    // ⚠️ La clé de cache DOIT inclure la façade : deux sites d'une même org
    // partagent org.id mais n'ont pas les mêmes événements (cf. virtualId
    // ci-dessous). Sans ça, le 1er site visité servirait ses événements au second.
    const selected=embeddedSelection(typeof window==='undefined'?null:window.location.href)
    const cacheKey = (virtualId ? orgId + "--" + virtualId : orgId)+JSON.stringify(selected)
    if (!force && cache.has(cacheKey)) return cache.get(cacheKey)

    const p = requestEvents(orgId, virtualId).then(data=>filterEmbeddedEvents(data,selected)).catch(() => {
        // A failed fetch is retryable. Never pin an empty catalogue in memory
        // for the remainder of the visit after a transient outage.
        if (cache.get(cacheKey) === p) cache.delete(cacheKey)
        return {...EMPTY, error: true}
    })
    cache.set(cacheKey, p)
    return p
}

async function requestEvents(orgId, virtualId = null) {
    // On passe la FAÇADE explicitement : l'identifiant de l'URL est ici org.id
    // (UUID), qui ne peut pas matcher un domain/prefix de virtual côté API —
    // sans ce paramètre, les événements scopés par site seraient tous filtrés.
    const query = virtualId ? "?virtualId=" + encodeURIComponent(virtualId) : ""
    const res = await ApiService.get("/events/" + orgId + query)
    if (res && res.success) {
        return {
            events: Array.isArray(res.events) ? res.events : [],
            eventGroupes: Array.isArray(res.eventGroupes) ? res.eventGroupes : [],
            eventGroupesByExtension: res.eventGroupesByExtension || {},
        }
    }
    throw new Error('EVENTS_UNAVAILABLE')
}

export default fetchEvents

// Hook React pour les composants FONCTION : fetch client (une fois, dédupliqué par
// org) de la vue unifiée des événements. Retourne { events, eventGroupes,
// eventGroupesByExtension, loading }. Suit le pattern ArticlesPreview (fetch dans un
// useEffect + garde `cancelled`). Ne fait rien si l'org est en accès limité.
export function useEvents(context) {
    const org = context?.org
    const [data, setData] = React.useState(EMPTY)
    const [loading, setLoading] = React.useState(true)
    const [attempt, setAttempt] = React.useState(0)
    const retry = React.useCallback(() => setAttempt(value => value + 1), [])

    React.useEffect(() => {
        if (!org || org.limited) { setData(EMPTY); setLoading(false); return }
        let cancelled = false
        setData(EMPTY)
        setLoading(true)
        // org.virtualId = façade réellement servie (posée au SSR par /v1/organisations
        // quand l'hôte/prefix matche un virtual) — même source que Form/index.js.
        fetchEvents(org.id, {virtualId: org.virtualId || null}).then(res => {
            if (!cancelled) { setData(res); setLoading(false) }
        })
        return () => { cancelled = true }
    }, [org?.id, org?.virtualId, org?.limited, attempt])

    React.useEffect(() => {
        if (!data.error || !org || org.limited) return
        const resume = () => {
            if (document.visibilityState !== 'hidden' && navigator.onLine !== false) retry()
        }
        window.addEventListener('online', resume)
        document.addEventListener('visibilitychange', resume)
        return () => {window.removeEventListener('online',resume);document.removeEventListener('visibilitychange',resume)}
    }, [data.error, org?.id, org?.virtualId, org?.limited, retry])

    return {...data, loading, retry}
}
